import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { DriverApi } from "../driver-api.ts";
import {
  capturedLine,
  COMPLETE_DELIVERY_UNAVAILABLE,
  proofChip,
  proofNote,
  proofThumbs,
  proofWordingIsDesigned,
  RECIPIENT_ABSENCE,
  type ProofRecord,
} from "../proof.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroSignature } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `8 Proof` — the proof of delivery, and whether DEL-07 has accepted it.
 *
 * The design: the `sign` hero, the dashed `proof pending` pill, _"Delivered to Amina K."_,
 * _"Signature and photo captured · checking…"_, three thumbnails, the dashed note _"Shown as
 * delivered only once proof is verified."_, and a slide-to-confirm `Complete delivery`.
 *
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **degraded**, with exactly one
 * element missing, and that element is the headline's second half:
 *
 *   > `Delivered to Amina K.` — who took the parcel — nothing publishes `proof.recipientName` or
 *   > `proof.evidence.recipientName`
 *
 * So the headline is `Delivered to` and stops there, with the design's own `unknown` mark after it,
 * and the signature hero keeps its dashed card and writes no name across it. The recipient's name
 * appears nowhere on this screen as a value, a dash or a zero. `proof.ts` and `Hero.tsx` carry the
 * reasoning for each.
 *
 * ## Which proof, and why it has to be told
 *
 * `GET /v1/proofs/{proofId}` names one proof, and nothing published lets a courier's application
 * find out which proof is theirs: DEL-07 publishes no listing, and the delivery a courier is on is
 * the absence `4 To pickup` and `Multi-stop job` are blocked on. So the identifier is read from the
 * address — `?proof=<uuid>` — which is the same arrangement screen 2 has for `?offers=`, and for the
 * same reason: a notification through NTF would open the application at it. R-33 and Q-77.
 */

export interface ProofProps {
  readonly api: DriverApi;
  /** The proof this screen was told about, from the address. Absent is a state, not an error. */
  readonly proofId: string | undefined;
}

export function Proof(props: ProofProps): ReactNode {
  const { api, proofId } = props;
  const [proof, setProof] = useState<ProofRecord | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (proofId === undefined) return;
    setState({ kind: "loading" });
    api
      .deliveryProof(proofId)
      .then((answer) => {
        setProof(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(stateForError(error, ["delivery-proof:read"]));
      });
  }, [api, proofId]);

  useEffect(load, [load]);

  if (proofId === undefined) {
    /*
     * No proof named, which is not a failure.
     *
     * A courier reaching this screen with nothing in the address has not hit an error: there is
     * nothing to look up. Said in the design's own vocabulary with this build's words, because the
     * design draws no such state — the same treatment screen 2 gives a stream with nothing to watch.
     */
    return (
      <StateScreen
        screen="proof"
        chip={{ class: "unknown", label: "no proof named" }}
        title="There's no proof to show"
        sub="This screen needs the proof it should show, and nothing published lets this app find out which one is yours."
        flag="wording not in the design"
      />
    );
  }

  if (state.kind !== "ready" || proof === undefined) {
    return (
      <StateScreen
        screen="proof"
        state={state}
        onRetry={load}
        purpose="Checking whether your proof has been verified."
      />
    );
  }

  return (
    <PhoneShell
      screen="proof"
      /*
       * `art:'sign'` — the design's dashed card, with the recipient's handwriting left out because
       * nothing published says whose it is. `Hero.tsx` carries the full note; Q-76 carries the
       * question of what to draw instead of a signature nobody can fetch.
       */
      art={<HeroSignature because={RECIPIENT_ABSENCE} />}
      /* `on:1`, the design's own flag. The same reading the three offer screens take. */
      online
      earningsLabel={null}
      chip={proofChip(proof)}
      title="Delivered to"
      titleAbsence={RECIPIENT_ABSENCE}
      sub={capturedLine(proof)}
      thumbs={proofThumbs(proof)}
      note={proofNote(proof)}
      actions={[
        {
          label: "Complete delivery",
          glyph: "done_all",
          primary: true,
          /*
           * `slide`, which is the design's fourth element on this button and not decoration:
           * _"slide-to-confirm for accept, arrive, start and complete so a pocket tap can't trigger
           * them."_ Completing a delivery is the last of those four.
           */
          slide: true,
          /*
           * The design's slide-to-confirm, drawn, and honest about why it cannot be slid.
           *
           * The designed act is creating the proof — which is the route the measurement names behind
           * this screen's first element, and it exists. What does not exist is any way for a
           * courier's application to learn the six identifiers DEL-07 requires on the body, because
           * every one of them describes the job and no job read is published to a courier.
           */
          unavailable: COMPLETE_DELIVERY_UNAVAILABLE,
        },
      ]}
      {...(proofWordingIsDesigned(proof) ? {} : { flag: "wording not in the design" })}
    />
  );
}
